/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useState } from "react";

import { COLORS } from "@/styles/colors";

import type { Payjp } from "@/lib/payjp/type";

import { usePayjpClient } from "@/hooks/use-payjp-client";

import { payjpCardHolderNameZodSchema } from "@/components/features/_shared/validation-schema/validation-schema";

const ELEMENT_STYLE = {
	base: {
		"::placeholder": {
			fontSize: "14px",
		},
		fontSize: "14px",
		fontFamily: `"游ゴシック体", YuGothic, "游ゴシック Medium", "Yu Gothic Medium", "游ゴシック", "Yu Gothic", sans-serif`,
		lineHeight: 1.5,
	},
	invalid: {
		color: COLORS.ERROR[600],
	},
};

const VALIDATION_STATUS_OF_ELEMENTS = {
	cardNumber: true,
	cardExpiry: true,
	cardCvc: true,
	cardHolderName: true,
};

export const usePayjpCreditForm = (
	onSetTargetElement: (element: HTMLElement) => void,
	onChangeHasPayjpValidation: (hasError: boolean) => void,
	_onChangeCardHolderName: (value: string) => void,
) => {
	const { createPayjpElements, createPayjpElement } = usePayjpClient();

	const [_, setValidationStatusOfElements] = useState(VALIDATION_STATUS_OF_ELEMENTS);

	const _onChangeHasPayjpValidation = useCallback(
		(newStatus: { [key in keyof typeof VALIDATION_STATUS_OF_ELEMENTS]?: boolean }) => {
			setValidationStatusOfElements((prev) => {
				const newValidationStatusOfElements = { ...prev, ...newStatus };
				onChangeHasPayjpValidation(Object.values(newValidationStatusOfElements).some((status) => status));
				return newValidationStatusOfElements;
			});
		},
		[onChangeHasPayjpValidation],
	);

	const [errorMessage, setErrorMessage] = useState("");

	const [cardHolderName, setCardHolderName] = useState("");
	const onChangeCardHolderName = useCallback(
		(value: string) => {
			const result = payjpCardHolderNameZodSchema.safeParse(value);

			if (!result.success) {
				setErrorMessage(result.error.errors[0].message);
				_onChangeHasPayjpValidation({ cardHolderName: true });
			} else {
				setErrorMessage("");
				_onChangeHasPayjpValidation({ cardHolderName: false });
				_onChangeCardHolderName(value);
			}

			setCardHolderName(value);
		},
		[_onChangeCardHolderName, _onChangeHasPayjpValidation],
	);

	const onChangeCardNumberElement: Payjp.Event.Element.OnChangeArgs.Listener = useCallback(
		(event) => {
			_onChangeHasPayjpValidation({ cardNumber: event.complete ? false : true });
		},
		[_onChangeHasPayjpValidation],
	);

	const onChangeCardExpiryElement: Payjp.Event.Element.OnChangeArgs.Listener = useCallback(
		(event) => {
			_onChangeHasPayjpValidation({ cardExpiry: event.complete ? false : true });
		},
		[_onChangeHasPayjpValidation],
	);

	const onChangeCardCvcElement: Payjp.Event.Element.OnChangeArgs.Listener = useCallback(
		(event) => {
			_onChangeHasPayjpValidation({ cardCvc: event.complete ? false : true });
		},
		[_onChangeHasPayjpValidation],
	);

	useEffect(() => {
		// Payjp の要素を作成する
		const payjpElements = createPayjpElements("ja");
		// form の要素を作成する
		const cardNumberElement = createPayjpElement(payjpElements, "cardNumber", {
			style: ELEMENT_STYLE,
		});
		const cardExpiryElement = createPayjpElement(payjpElements, "cardExpiry", {
			style: ELEMENT_STYLE,
		});
		const cardCvcElement = createPayjpElement(payjpElements, "cardCvc", { style: ELEMENT_STYLE });

		if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
			throw new Error("Payjp の要素を作成できませんでした");
		}

		// form を mount する
		cardNumberElement.mount("#card-number");
		cardExpiryElement.mount("#card-expiry");
		cardCvcElement.mount("#card-cvc");

		// 情報を登録する際に、Payjp へ送信する form の element を設定する
		onSetTargetElement(cardNumberElement);

		// form の event を監視する
		cardNumberElement.on("change", onChangeCardNumberElement);
		cardExpiryElement.on("change", onChangeCardExpiryElement);
		cardCvcElement.on("change", onChangeCardCvcElement);

		return () => {
			// form を削除する
			cardNumberElement.unmount();
			cardExpiryElement.unmount();
			cardCvcElement.unmount();
		};
		/**
		 * 本来は依存配列に `onChangeHasPayjpValidation` を含めるべきだが、含めてしまうとelementが再生成されてしまうため、現状は含めていない。
		 * @todo ↑ の問題の修正
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createPayjpElements, createPayjpElement, onSetTargetElement]);

	return {
		cardHolderName,
		onChangeCardHolderName,
		errorMessage,
	};
};
